import React, { useState, useEffect } from 'react';
import {
  Award,
  Clock,
  CheckCircle2,
  TrendingUp,
  RotateCcw,
  Sparkles,
  Zap,
  BookOpen,
  Layers,
  ArrowRight,
  Compass,
  Activity,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import { User, Subscription, ExamSession, ExtendedTrackType, ExamMode, isProSubscriber } from '../../shared/types';
import { api } from '../lib/api';

export interface DashboardPageProps {
  user: User | null;
  subscription: Subscription | null;
  onStartExam: (track: ExtendedTrackType, mode: ExamMode) => void;
  onOpenAuthModal: () => void;
  onNavigate?: (path: string) => void;
}

export function DashboardPage({
  user,
  subscription,
  onStartExam,
  onOpenAuthModal,
  onNavigate,
}: DashboardPageProps) {
  const [examHistory, setExamHistory] = useState<ExamSession[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const isPro = isProSubscriber(subscription);

  const handleManageBilling = async () => {
    try {
      setIsPortalLoading(true);
      const res = await api.billing.createPortalSession();
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      alert(err.message || 'Failed to open billing portal');
      setIsPortalLoading(false);
    }
  };

  useEffect(() => {
    async function loadHistory() {
      if (!user) return;
      try {
        setIsLoadingHistory(true);
        const data = await api.exams.getHistory();
        setExamHistory(data.sessions || []);
      } catch (err) {
        console.error('Failed to load history', err);
      } finally {
        setIsLoadingHistory(false);
      }
    }
    loadHistory();
  }, [user]);

  if (!user) {
    return (
      <div className="container max-w-2xl py-20 text-center space-y-6">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 dark:text-amber-400 mb-2">
          <Award className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground dark:text-white">Access Your Candidate Dashboard</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Sign in or create an account to view your mock test scores, diagnostic strengths, and track your readiness for the Ramsay Assessment.
        </p>
        <Button variant="amber" size="lg" onClick={onOpenAuthModal} className="px-8 shadow-md shadow-amber-500/20">
          Sign In / Create Account
        </Button>
      </div>
    );
  }

  const completedExams = examHistory.filter((e) => e.status === 'completed');
  const avgScore =
    completedExams.length > 0
      ? Math.round(
          (completedExams.reduce((sum, e) => sum + ((e.score || 0) / (e.totalQuestions || 1)) * 100, 0) /
            completedExams.length)
        )
      : 0;

  return (
    <div className="container max-w-5xl py-8 space-y-8 pb-16">
      {/* User Banner */}
      <div className="rounded-2xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/80 p-6 md:p-8 backdrop-blur-md shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="h-14 w-14 rounded-2xl object-cover border-2 border-amber-500/40 shadow-md shadow-amber-500/10"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-xl">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground dark:text-white tracking-tight">
                  Welcome back, {user.name}
                </h1>
                <Badge
                  variant={isPro ? 'success' : 'warning'}
                  className="text-[10px] font-bold"
                >
                  {subscription?.status === 'trialing'
                    ? 'Pro (14-Day Trial)'
                    : isPro
                    ? 'Pro Active'
                    : 'Free Tier'}
                </Badge>
                {user.authProvider === 'google' && (
                  <Badge variant="outline" className="text-[10px] border-blue-500/40 text-blue-600 dark:text-blue-400">
                    Google Account
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{user.email}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isPro ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleManageBilling}
                disabled={isPortalLoading}
                className="text-xs"
              >
                {isPortalLoading ? 'Loading...' : 'Manage Subscription'}
              </Button>
            ) : (
              <Button
                variant="amber"
                size="sm"
                onClick={() => onNavigate && onNavigate('/pricing')}
                className="text-xs font-bold gap-1 shadow-sm shadow-amber-500/20"
              >
                <Zap className="h-3 w-3 fill-current" />
                14-Day Free Trial
              </Button>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={() => onStartExam('all', 'study')}
              className="text-xs gap-1.5 shadow-sm"
            >
              <BookOpen className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
              Study Mode
            </Button>
            <Button
              variant="amber"
              size="sm"
              onClick={() => onStartExam('all', 'timed_mock')}
              className="text-xs gap-1.5 font-bold shadow-md shadow-amber-500/20"
            >
              <Zap className="h-3.5 w-3.5 fill-current" />
              Start Timed Mock
            </Button>
          </div>
        </div>

        {/* Free Tier Trial Invitation Banner inside Dashboard */}
        {!isPro && (
          <div className="mt-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-300">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                14-Day Free Trial Available
              </div>
              <p className="text-xs text-muted-foreground">
                Get unlimited access to all 4 test tracks, 60-min timed mocks, and electrical/hydraulic schematics for 14 days free. Then $9.99/mo. Cancel anytime.
              </p>
            </div>
            <Button
              variant="amber"
              size="sm"
              onClick={() => onNavigate && onNavigate('/pricing')}
              className="text-xs font-bold shrink-0 shadow-sm shadow-amber-500/20"
            >
              Start Free Trial
            </Button>
          </div>
        )}

        {/* Aggregate Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-border dark:border-slate-800">
          <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-xl border border-border dark:border-slate-800/80">
            <span className="text-xs text-muted-foreground block">Tests Completed</span>
            <span className="text-2xl font-bold text-foreground dark:text-white font-mono mt-1 block">
              {completedExams.length}
            </span>
          </div>

          <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-xl border border-border dark:border-slate-800/80">
            <span className="text-xs text-muted-foreground block">Average Accuracy</span>
            <span className={`text-2xl font-bold font-mono mt-1 block ${avgScore >= 70 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {avgScore}%
            </span>
          </div>

          <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-xl border border-border dark:border-slate-800/80">
            <span className="text-xs text-muted-foreground block">Passing Target</span>
            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-1 block">
              70.0%
            </span>
          </div>

          <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-xl border border-border dark:border-slate-800/80">
            <span className="text-xs text-muted-foreground block">Status</span>
            <span className="text-sm font-bold text-foreground dark:text-slate-200 mt-2 block">
              {avgScore >= 70 ? 'Ready for Exam' : 'Needs Review'}
            </span>
          </div>
        </div>
      </div>

      {/* Interactive Study Lab & Visual Practice Modules */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground dark:text-white tracking-tight flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Specialized Study Lab &amp; Visual Tools
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate && onNavigate('/study-lab')}
            className="text-xs text-amber-600 dark:text-amber-400 gap-1 font-bold"
          >
            Open All Labs <ArrowRight className="h-3 w-3" />
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Spatial Relations Lab */}
          <div
            onClick={() => onNavigate && onNavigate('/study-lab')}
            className="cursor-pointer group p-5 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-card to-purple-500/5 hover:border-purple-500/60 transition-all shadow-sm space-y-2.5"
          >
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 rounded-xl bg-purple-500/15 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                <Compass className="h-5 w-5" />
              </div>
              <Badge variant="secondary">USPS 955</Badge>
            </div>
            <h3 className="font-bold text-sm text-foreground group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
              Spatial Relations &amp; 3D Lab
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Cube net unfolding, orthographic projections, and interactive gear train direction trainers.
            </p>
          </div>

          {/* Interactive Schematics Simulator */}
          <div
            onClick={() => onNavigate && onNavigate('/study-lab')}
            className="cursor-pointer group p-5 rounded-2xl border border-sky-500/30 bg-gradient-to-br from-card to-sky-500/5 hover:border-sky-500/60 transition-all shadow-sm space-y-2.5"
          >
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                <Activity className="h-5 w-5" />
              </div>
              <Badge variant="secondary">Interactive</Badge>
            </div>
            <h3 className="font-bold text-sm text-foreground group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
              Schematics &amp; 4-20mA Simulator
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Interactive 3-wire motor starter ladder circuit and 4-20 mA analog transmitter scaling.
            </p>
          </div>

          {/* Flashcards & Formulas */}
          <div
            onClick={() => onNavigate && onNavigate('/study-lab')}
            className="cursor-pointer group p-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-card to-amber-500/5 hover:border-amber-500/60 transition-all shadow-sm space-y-2.5"
          >
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <Badge variant="warning">Quick Drills</Badge>
            </div>
            <h3 className="font-bold text-sm text-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
              Flashcards &amp; Formula Sheet
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Drill key formulas (Ohm’s law, synchronous speed, sling angle tension, bolt grades).
            </p>
          </div>
        </div>
      </div>

      {/* Past Exam Attempts History */}
      <div>
        <h2 className="text-lg font-bold text-foreground dark:text-white tracking-tight mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500 dark:text-amber-400" />
          Recent Exam Attempts
        </h2>

        {isLoadingHistory ? (
          <div className="py-8 text-center text-xs text-muted-foreground">Loading exam records...</div>
        ) : examHistory.length === 0 ? (
          <Card className="border-border dark:border-slate-800 bg-card/60 dark:bg-slate-950/40 p-8 text-center shadow-sm">
            <p className="text-sm text-muted-foreground mb-4">No exam sessions completed yet.</p>
            <Button variant="amber" size="sm" onClick={() => onStartExam('all', 'timed_mock')}>
              Take Your First Test
            </Button>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {examHistory.map((session) => {
              const totalQ = (session as any).total_questions || session.totalQuestions || 1;
              const score = session.score || 0;
              const pct = Math.round((score / totalQ) * 100);
              const isPass = pct >= 70;
              const started = (session as any).started_at || session.startedAt;
              const dateStr = started ? new Date(started * 1000).toLocaleDateString() : '';

              return (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-900/60 text-xs text-foreground dark:text-slate-300 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={session.track as any}>
                      {session.track}
                    </Badge>
                    <div>
                      <span className="font-semibold text-foreground dark:text-white uppercase tracking-wider block">
                        {session.mode === 'study' ? 'Study Mode' : 'Timed Mock Exam'}
                      </span>
                      <span className="text-muted-foreground text-[11px]">{dateStr}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono font-bold text-foreground dark:text-white text-sm">
                        {session.score}/{session.totalQuestions} ({pct}%)
                      </div>
                      <Badge variant={isPass ? 'success' : 'warning'} className="text-[9px] py-0">
                        {isPass ? 'Pass' : 'Review'}
                      </Badge>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onStartExam(session.track, session.mode)}
                      className="text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retake
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
